import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from '@material-ui/core';
import { darken } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { InfoBox } from './InfoBox';
import { formatFloat } from '../utils/numberFormatters';
import type { TerminalOverviewGroup } from '../hooks/useTerminalOverviewStats';

interface TerminalOverviewProps {
  groups: TerminalOverviewGroup[];
}

export const TerminalOverview: React.FC<TerminalOverviewProps> = ({ groups }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const styles = useMemo(
    () => ({
      headerCell: {
        fontWeight: 700,
        fontSize: '0.75rem',
        padding: '3px 2px',
        borderBottom: 'none',
      },
      labelCell: {
        fontWeight: 700,
        fontSize: '0.85rem',
        color: theme.palette.primary.main,
        padding: '4px 2px',
        borderBottom: 'none',
        whiteSpace: 'nowrap' as const,
        cursor: 'default',
      },
      valueCell: {
        fontSize: '0.83rem',
        padding: '4px 2px',
        borderBottom: 'none',
      },
    }),
    [theme]
  );

  const columns = useMemo(
    () => [
      { key: 'total' as const,    label: translate('osfc.storage_ui.terminal_overview.total'),    color: theme.palette.primary.main },
      { key: 'empty' as const,    label: translate('osfc.storage_ui.terminal_overview.empty'),    color: theme.palette.storageUi.emptyTextColor },
      { key: 'occupied' as const, label: translate('osfc.storage_ui.terminal_overview.occupied'), color: darken(theme.palette.storageUi.occupied, 0.25) },
      { key: 'blocked' as const,  label: translate('osfc.storage_ui.terminal_overview.blocked'),  color: darken(theme.palette.storageUi.blocked, 0.38) },
    ],
    [theme, translate]
  );

  return (
    <InfoBox title={translate('osfc.storage_ui.terminal_overview.title')} style={{ margin: '15px 0', padding: '10px' }}>
      <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...styles.headerCell, width: '13%' }} />
            {columns.map(({ key, label, color }) => (
              <TableCell
                key={key}
                align="right"
                style={{ ...styles.headerCell, width: '21.75%', color }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TableRow key={group.label}>
              <Tooltip title={group.label} placement="right" arrow>
                <TableCell style={styles.labelCell}>{group.abbr}</TableCell>
              </Tooltip>
              {columns.map(({ key, color }) => (
                <TableCell
                  key={key}
                  align="right"
                  style={{ ...styles.valueCell, color }}
                >
                  {key === 'occupied' ? (
                    <Tooltip title={`${formatFloat(group.occupiedRate)}%`} placement="top" arrow>
                      <span style={{ cursor: 'default' }}>{group[key]}</span>
                    </Tooltip>
                  ) : (
                    group[key]
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </InfoBox>
  );
};
